import React, { useState, useEffect, useCallback, memo } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Container, TextField, Button, Box, MenuItem, Autocomplete, Divider,
    Typography, IconButton, Snackbar, Alert, Chip, Dialog
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentIcon from '@mui/icons-material/Payment';
import CloseIcon from '@mui/icons-material/Close';
import FormSection from './FormSection';
import ConfirmDialog from './ConfirmDialog';
import CustomDatePicker from './CustomDatePicker';
import BillDetailDialog from './BillDetailDialog';
import BulkPaymentDialog from './BulkPaymentDialog';
import FastSearchSection from './booking/FastSearchSection';
import TicketExportDialog from './booking/TicketExportDialog';
import InvoiceExportDialog from './booking/InvoiceExportDialog';
import CancellationDialog from './CancellationDialog';
import CurrencyInput from './CurrencyInput';

import * as API from '../api';
import { formatDateDisplay } from '../utils/dateUtils';
import { formatCurrency, parseCurrency } from '../utils/currencyUtils';
import { INITIAL_STATE, ITEM_TEMPLATES, BOOKING_STATUS_OPTIONS, OPS_STATUS_OPTIONS } from '../utils/bookingConstants';
import { CustomLabel, rowStyle, GuestItem, PaymentItem, RoomItem, ExtraItem, TransportItem, TourItem, CustomerChargeItem, LiabilityItem } from './booking/BookingFormItems';

const formTheme = createTheme({
    typography: {
        fontFamily: "'Roboto', 'Arial', sans-serif", // Khắc phục lỗi hiển thị font
        button: {
            textTransform: 'none', // Khắc phục lỗi chữ I giống chữ l do viết hoa
            fontWeight: 'bold'
        },
        allVariants: {
            fontFeatureSettings: "'cv11', 'ss01'",
        }
    },
    shape: { borderRadius: 8 },
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: { backgroundColor: '#fff', '&.Mui-disabled': { backgroundColor: '#f8f6f6ff' } }
            }
        }
    }
});

function BookingForm() {
    const location = useLocation();
    const [formData, setFormData] = useState(INITIAL_STATE);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [searchKey, setSearchKey] = useState(0);

    const [tourOptions, setTourOptions] = useState([]);
    const [saleOwnerOptions, setSaleOwnerOptions] = useState([]);
    const [transportSupplierOptions, setTransportSupplierOptions] = useState([]);
    const [transportRouteOptions, setTransportRouteOptions] = useState([]);
    const [hotelRoomOptions, setHotelRoomOptions] = useState([]);
    const [financialAccounts, setFinancialAccounts] = useState([]);
    const [currentRate, setCurrentRate] = useState(25000);

    const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
    const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ open: false, title: '', content: '', action: null, confirmColor: 'primary' });

    const [custConflictDialog, setCustConflictDialog] = useState({ 
        open: false, 
        existingName: '', 
        newName: '', 
        phone: '' 
    });

    const [bulkPayModal, setBulkPayModal] = useState({ open: false, items: [], type: 'PAYABLE' });
    const [billModal, setBillModal] = useState({ open: false, data: null });
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const [passportPreview, setPassportPreview] = useState({ open: false, url: '' });

    const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });
    const handleCloseToast = (event, reason) => {
        if (reason === 'clickaway') return;
        setToast({ ...toast, open: false });
    };

    useEffect(() => {
        const loadCatalogs = async () => {
            try {
                // CHÚ Ý DÒNG NÀY: Phải có chữ 'accounts' ở cuối mảng khai báo
                const [tours, users, providers, routes, hotelRooms, accounts] = await Promise.all([
                    API.fetchTours(), 
                    API.fetchUsers(), 
                    API.fetchAllServiceProviders(), 
                    API.fetchTransportRoutes(), 
                    API.fetchHotelRooms().catch(() => ({ data: { results: [] } })),
                    API.fetchAllFinancialAccounts().catch(() => ({ data: [] }))
                ]);
                
                setTourOptions(tours.data);
                setSaleOwnerOptions(users.data);
                setTransportSupplierOptions(providers.data);
                setTransportRouteOptions(routes.data || routes.data?.results || []);
                setHotelRoomOptions(hotelRooms.data?.results || hotelRooms.data || []);
                
                // Gán dữ liệu tài khoản ngân hàng
                setFinancialAccounts(accounts.data || []); 
                
                try {
                    const rateRes = await API.getExchangeRate(); 
                    if(rateRes.data && rateRes.data.rate) setCurrentRate(parseFloat(rateRes.data.rate));
                } catch (e) { console.warn("Không lấy được tỷ giá, dùng mặc định 25,000"); }
            } catch (err) { console.error("Lỗi tải danh mục:", err); }
        };
        loadCatalogs();
    }, []);

    const mapPayableToForm = (fullData, transportOptions = [], toursList = []) => {
        const availableSuppliers = transportOptions.length > 0 ? transportOptions : transportSupplierOptions;
        const availableTours = toursList.length > 0 ? toursList : tourOptions;
        const payables = fullData.payables || [];

        const mappedPayments = (fullData.payments || []).map(p => {
            let resolvedSupplier = p.supplier;
            if (typeof resolvedSupplier === 'number' || typeof resolvedSupplier === 'string') {
                resolvedSupplier = transportOptions.find(opt => opt.id === resolvedSupplier) || null;
            }
            return {
                ...p,
                supplier: resolvedSupplier,
                amount: parseFloat(p.amount || 0),
                amount_original: parseFloat(p.amount || 0),
                currency: 'VND',
                rate: 1,
                receive_account_name: p.receive_account_name,
                sender_info: p.sender_info
            };
        });

        const roomPayables = [...payables.filter(p => p.payable_type === 'ROOM')];
        const mappedRooms = (fullData.rooms || []).map((room) => {
            const pIndex = roomPayables.findIndex(rp => rp.supplier == room.supplier && parseFloat(rp.amount) === parseFloat(room.cost_price * room.quantity));
            let p = null;
            if (pIndex !== -1) {
                p = roomPayables[pIndex];
                roomPayables.splice(pIndex, 1); // Cắt bỏ cái đã map thành công
            }
            let resolvedSupplier = room.supplier;
            if (typeof resolvedSupplier === 'number' || typeof resolvedSupplier === 'string') {
                resolvedSupplier = availableSuppliers.find(opt => opt.id === resolvedSupplier) || null;
            }
            return {
                ...room,
                payable_id: p ? p.id : null,
                supplier: resolvedSupplier,
                operation_status: room.operation_status || 'OPEN',
                payment_method: room.payment_method || 'INCLUDED',
                cost_price: parseFloat(room.cost_price),
                cost_original: parseFloat(room.cost_original),
                cost_currency: room.cost_currency || 'VND',
                cost_rate: parseFloat(room.cost_rate) || 1,
                selling_price: parseFloat(room.selling_price),
                selling_original: parseFloat(room.selling_original),
                selling_currency: room.selling_currency || 'VND',
                selling_rate: parseFloat(room.selling_rate) || 1,
                payment_status: p ? p.status : 'UNPAID',
                bill_image: p ? p.bill_image : null,
                note_payment: p ? p.note : '',
                payment_account_name: p ? p.payment_account_name : null,
                receiver_info: p ? p.receiver_info : null,
                paid_at: p ? p.paid_at : null
            };
        });

        const transportPayables = [...payables.filter(p => ['TRANSPORT_TO', 'TRANSPORT_FROM', 'TRANSPORT_ROUND'].includes(p.payable_type))];
        const mappedTransports = (fullData.transports || []).map(trans => {
            let type = 'EXTRA';
            if (trans.direction === 'ONE_WAY_TO') type = 'TRANSPORT_TO';
            else if (trans.direction === 'ONE_WAY_FROM') type = 'TRANSPORT_FROM';
            else if (trans.direction === 'ROUND_TRIP') type = 'TRANSPORT_ROUND';

            const pIndex = transportPayables.findIndex(item => item.payable_type === type && (item.supplier === trans.supplier || item.supplier === (trans.supplier?.id)));
            let p = null;
            if (pIndex !== -1) {
                p = transportPayables[pIndex];
                transportPayables.splice(pIndex, 1); // Cắt bỏ cái đã map thành công
            }
            let resolvedSupplier = trans.supplier;
            if (typeof resolvedSupplier === 'number' || typeof resolvedSupplier === 'string') {
                resolvedSupplier = availableSuppliers.find(opt => opt.id === resolvedSupplier) || null;
            }
            return {
                ...trans,
                payable_id: p ? p.id : null,
                operation_status: trans.operation_status || 'OPEN',
                payment_method: trans.payment_method || 'SEPARATE',
                supplier: resolvedSupplier,
                pickup_datetime: trans.pickup_datetime ? trans.pickup_datetime.substring(0, 16) : '',
                dropoff_point: trans.dropoff_point || '',
                cost_price: parseFloat(trans.cost_price) || 0,
                cost_original: parseFloat(trans.cost_original) || parseFloat(trans.cost_price) || 0,
                cost_currency: trans.cost_currency || 'VND',
                cost_rate: parseFloat(trans.cost_rate) || 1,
                selling_price: parseFloat(trans.selling_price) || 0,
                selling_original: parseFloat(trans.selling_original) || parseFloat(trans.selling_price) || 0,
                selling_currency: trans.selling_currency || 'VND',
                selling_rate: parseFloat(trans.selling_rate) || 1,
                payment_status: p ? p.status : 'UNPAID',
                bill_image: p ? p.bill_image : null,
                note: p ? p.note : '',
                payment_account_name: p ? p.payment_account_name : null,
                receiver_info: p ? p.receiver_info : null,
                paid_at: p ? p.paid_at : null
            };
        });

        const extraPayables = [...payables.filter(p => p.payable_type === 'EXTRA')];
        const mappedExtras = (fullData.extras || []).map((extra) => {
            let pIndex = extraPayables.findIndex(ep => 
                ep.supplier == extra.supplier && Math.abs(parseFloat(ep.amount || 0) - parseFloat((extra.cost_price || 0) * (extra.quantity || 1))) < 100
            );

            if (pIndex === -1) {
                pIndex = extraPayables.findIndex(ep => ep.supplier == extra.supplier);
            }

            let p = null;
            if (pIndex !== -1) {
                p = extraPayables[pIndex];
                extraPayables.splice(pIndex, 1);
            }

            let resolvedSupplier = extra.supplier;
            if (typeof resolvedSupplier === 'number' || typeof resolvedSupplier === 'string') {
                resolvedSupplier = availableSuppliers.find(opt => opt.id === resolvedSupplier) || null;
            }
            return {
                ...extra,
                payable_id: p ? p.id : null,
                supplier: resolvedSupplier,
                operation_status: extra.operation_status || 'OPEN',
                payment_method: extra.payment_method || 'SEPARATE',
                quantity: parseInt(extra.quantity) || 1,
                cost_price: parseFloat(extra.cost_price),
                cost_original: parseFloat(extra.cost_original),
                cost_currency: extra.cost_currency || 'VND',
                cost_rate: parseFloat(extra.cost_rate) || 1,
                selling_price: parseFloat(extra.selling_price),
                selling_original: parseFloat(extra.selling_original),
                selling_currency: extra.selling_currency || 'VND',
                selling_rate: parseFloat(extra.selling_rate) || 1,
                payment_status: p ? p.status : 'UNPAID', 
                bill_image: p ? p.bill_image : null,
                note: extra.note || '',
                payment_account_name: p ? p.payment_account_name : null,
                receiver_info: p ? p.receiver_info : null,
                paid_at: p ? p.paid_at : null
            };
        });

        const allRefunds = payables.filter(p => p.payable_type === 'REFUND');
        let mainRefund = null;
        let serviceRefundsRaw = [];

        if (allRefunds.length > 0) {
            if (fullData.booking_status === 'CANCELLED' || fullData.cancellation_reason || fullData.refund_amount > 0) {
                mainRefund = allRefunds[0];
                serviceRefundsRaw = allRefunds.slice(1);
            } else {
                serviceRefundsRaw = allRefunds;
            }
        }

        const mappedServiceRefunds = serviceRefundsRaw.map(ref => {
            let resolvedSupplier = ref.supplier;
            if (typeof resolvedSupplier === 'number' || typeof resolvedSupplier === 'string') {
                resolvedSupplier = availableSuppliers.find(opt => opt.id === resolvedSupplier) || null;
            }
            return {
                payable_id: ref.id,
                supplier: resolvedSupplier,
                amount: parseFloat(ref.amount || 0),
                amount_original: parseFloat(ref.amount_original || 0),
                currency: ref.currency || 'VND',
                rate: parseFloat(ref.exchange_rate) || 1,
                note: ref.note || '',
                receiver_info: ref.receiver_info || '',
                payment_status: ref.status || 'UNPAID',
                bill_image: ref.bill_image || null,
                payment_account_name: ref.payment_account_name || null,
                paid_at: ref.paid_at || null
            };
        });

        const mappedTours = (fullData.tours || []).map(t => {
            let resolvedSupplier = t.supplier;
            if (typeof resolvedSupplier === 'number' || typeof resolvedSupplier === 'string') {
                resolvedSupplier = availableSuppliers.find(opt => opt.id == resolvedSupplier) || null;
            }
            return {
                ...t,
                supplier: resolvedSupplier,
                operation_status: t.operation_status || 'OPEN',
                payment_method: 'SEPARATE'
            };
        });

        const mappedRawPayables = payables.filter(p => p.payable_type !== 'REFUND').map(p => {
            let resolvedSupplier = p.supplier;
            if (typeof resolvedSupplier === 'number' || typeof resolvedSupplier === 'string') {
                resolvedSupplier = availableSuppliers.find(opt => opt.id == resolvedSupplier) || null;
            }
            return {
                ...p,
                supplier: resolvedSupplier,
                quantity: p.quantity !== undefined ? p.quantity : 1,
                unit_price: p.unit_price !== undefined ? p.unit_price : parseFloat(p.amount || 0),
                unit_original: p.unit_original !== undefined ? p.unit_original : parseFloat(p.amount_original || 0),
            };
        });

        return {
            ...INITIAL_STATE,
            ...fullData,
            refund_payable_id: mainRefund ? mainRefund.id : null,
            refund_amount: mainRefund ? parseFloat(mainRefund.amount) : parseFloat(fullData.refund_amount || 0),
            refund_original: mainRefund ? parseFloat(mainRefund.amount_original) : parseFloat(fullData.refund_amount || 0),
            refund_currency: mainRefund ? mainRefund.currency : 'VND',
            refund_rate: mainRefund ? parseFloat(mainRefund.exchange_rate) : 1,
            refund_status: mainRefund ? (mainRefund.status === 'PAID' ? 'COMPLETED' : 'PENDING') : 'NONE',
            refund_bill_image: mainRefund ? mainRefund.bill_image : null,
            refund_account_info: mainRefund ? mainRefund.receiver_info : (fullData.refund_account_info || ''),
            cancellation_reason: fullData.cancellation_reason || (mainRefund ? mainRefund.note : ''),
            refund_bill_note: mainRefund ? mainRefund.note : '',
            service_refunds: mappedServiceRefunds,
            tours: mappedTours,             
            payables: mappedRawPayables,    
            payments: mappedPayments,
            transports: mappedTransports,
            rooms: mappedRooms,
            extras: mappedExtras,
            tour: availableTours.find(t => t.tour_code === fullData.tour) || fullData.tour,
            customer: fullData.customer_detail || (typeof fullData.customer === 'object' ? fullData.customer : null),
        };
    };

    const loadBooking = useCallback(async (code) => {
        try {
            const res = await API.getBookingDetail(code);
            const providersRes = await API.fetchAllServiceProviders();
            
            const providerList = Array.isArray(providersRes.data) ? providersRes.data : (providersRes.data.results || []);
            
            setTransportSupplierOptions(providerList);
            const newFormData = mapPayableToForm(res.data, providerList, tourOptions);
            
            setFormData(newFormData);
            setIsEditMode(true);
            showToast(`Đã mở Booking: ${code}`);
            window.history.replaceState({}, document.title);
        } catch (error) {
            console.error("Lỗi tải booking:", error);
            showToast("Không tải được dữ liệu booking", "error");
        }
    }, [tourOptions]);

    useEffect(() => {
        if (location.state && location.state.bookingCodeToLoad) {
            loadBooking(location.state.bookingCodeToLoad);
        }
    }, [location.state, tourOptions]);

    const handleChange = (e) => {
        let { name, value, type } = e.target;
        if (['total_price', 'net_tour_price'].includes(name)) value = parseCurrency(value);
        else if (type === 'number') value = value === '' ? 0 : parseInt(value, 10);

        setFormData(prev => {
            return { ...prev, [name]: value };
        });
        setIsDirty(true);
    };

    const handleMoneyChange = useCallback((listName, index, prefix, moneyObj) => {
        setFormData(prev => {
            // Khởi tạo mảng an toàn tránh lỗi undefined khi mảng mới tạo
            const list = [...(prev[listName] || [])];
            const item = { ...list[index] };

            // 1. Cập nhật dữ liệu tiền tệ vào item
            if (listName === 'payments') {
                item.amount_original = moneyObj.original;
                item.currency = moneyObj.currency;
                item.rate = moneyObj.rate;
                item.amount = moneyObj.vnd;
            } else if (listName === 'payables') {
                item.amount_original = moneyObj.original;
                item.currency = moneyObj.currency;
                item.exchange_rate = moneyObj.rate;
                item.amount = moneyObj.vnd;
            } else {
                item[`${prefix}_original`] = moneyObj.original;
                item[`${prefix}_currency`] = moneyObj.currency;
                item[`${prefix}_rate`] = moneyObj.rate;
                
                const valueField = prefix === 'amount' ? 'amount' : `${prefix}_price`;
                item[valueField] = moneyObj.vnd;
            }

            list[index] = item;
            
            // 2. Logic reset giá selling phòng KS (giữ nguyên từ bản cũ)
            if(listName === 'rooms' && item.payment_method === 'INCLUDED' && prefix === 'selling') {
                item.selling_price = 0;
                item.selling_original = 0;
            }

            let updates = { [listName]: list };

            // 3. Tự động cộng dồn Tổng giá Booking (Cả giá VND và Ngoại tệ)
            if (listName === 'customer_charges') {
                const newTotal = list.reduce((sum, it) => {
                    const qty = parseFloat(it.quantity) || 1; 
                    const unitPrice = parseFloat(it.unit_price) || 0; 
                    return sum + (qty * unitPrice);
                }, 0);
                const newOriginalTotal = list.reduce((sum, it) => {
                    const qty = parseFloat(it.quantity) || 1; 
                    const unitOriginal = parseFloat(it.unit_original) || 0; 
                    return sum + (qty * unitOriginal);
                }, 0);
                updates.total_price = newTotal;
                updates.original_total_price = newOriginalTotal;
            }

            return { ...prev, ...updates };
        });
        setIsDirty(true);
    }, []);

    const handleTotalPriceChange = useCallback((moneyObj) => {
        setFormData(prev => ({
            ...prev,
            original_total_price: moneyObj.original,
            currency: moneyObj.currency,
            exchange_rate: moneyObj.rate,
            total_price: moneyObj.vnd
        }));
        setIsDirty(true);
    }, []);

    const handleListChange = useCallback((listName, index, field, value) => {
        let finalVal = value;
        if (['cost_price', 'selling_price', 'quantity', 'unit_price'].includes(field)) finalVal = parseCurrency(value);
        setFormData(prev => {
            const list = [...(prev[listName] || [])];
            list[index] = { ...list[index], [field]: finalVal };
            
            let updates = { [listName]: list };
            
            // Tự động tính tổng thu khách
            if (listName === 'customer_charges') {
                const newTotal = list.reduce((sum, item) => sum + ((parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price) || 0)), 0);
                const newOriginalTotal = list.reduce((sum, item) => sum + ((parseFloat(item.quantity) || 1) * (parseFloat(item.unit_original) || 0)), 0);
                updates.total_price = newTotal;
                updates.original_total_price = newOriginalTotal;
            }
            return { ...prev, ...updates };
        });
        setIsDirty(true);
    }, []);

    const handleTransportRouteSelect = useCallback((index, routeObj) => {
        setFormData(prev => {
            const list = [...prev.transports];
            if (routeObj) {
                list[index] = {
                    ...list[index],
                    route: routeObj.route_code,
                    supplier: transportSupplierOptions.find(s => s.id === (routeObj.supplier?.id || routeObj.supplier)) || routeObj.supplier,
                    vehicle_type: routeObj.vehicle_type,
                    
                    // 1. Không tự động lấy điểm đón/trả từ DB nữa, giữ nguyên giá trị user đã gõ
                    pickup_point: list[index].pickup_point || '',
                    dropoff_point: list[index].dropoff_point || '', 
                    
                    // 2. Chỉ tự động điền giá của tuyến nếu user CHƯA nhập giá (giá = 0)
                    cost_price: list[index].cost_price > 0 ? list[index].cost_price : (parseFloat(routeObj.cost_price) || 0),
                    cost_original: list[index].cost_original > 0 ? list[index].cost_original : (parseFloat(routeObj.cost_price) || 0),
                    selling_price: list[index].selling_price > 0 ? list[index].selling_price : (parseFloat(routeObj.selling_price) || 0),
                    selling_original: list[index].selling_original > 0 ? list[index].selling_original : (parseFloat(routeObj.selling_price) || 0),
                };
            } else {
                list[index] = { ...list[index], route: null };
            }
            return { ...prev, transports: list };
        });
        setIsDirty(true);
    }, [transportSupplierOptions]);

    const handleTourSelect = useCallback((index, tourObj) => {
        setFormData(prev => {
            const list = [...(prev.tours || [])];
            if (tourObj) {
                // Khớp thông tin nhà cung cấp đại lý từ danh sách đối tác
                const supId = tourObj.supplier?.id || tourObj.supplier;
                const resolvedSupplier = transportSupplierOptions.find(s => s.id === supId) || tourObj.supplier;
                
                list[index] = {
                    ...list[index],
                    tour: tourObj.tour_code,
                    custom_tour_name: tourObj.name || '',
                    supplier: resolvedSupplier || null,
                    duration_days: tourObj.duration_days || 1,
                };
            } else {
                list[index] = {
                    ...list[index],
                    tour: '',
                    custom_tour_name: '',
                    supplier: null,
                    duration_days: 1
                };
            }
            return { ...prev, tours: list };
        });
        setIsDirty(true);
    }, [transportSupplierOptions]);

    const handleRoomCategorySelect = useCallback((index, roomObj) => {
        setFormData(prev => {
            const list = [...prev.rooms];
            if (roomObj) {
                list[index] = {
                    ...list[index],
                    room_category: roomObj.room_code, // Lưu mã hạng phòng chuẩn
                    room_type: roomObj.room_name,     // Gán tên phòng vào text tùy chỉnh
                    supplier: transportSupplierOptions.find(s => s.id === (roomObj.supplier?.id || roomObj.supplier)) || roomObj.supplier,
                    cost_price: parseFloat(roomObj.cost_price) || 0,
                    cost_original: parseFloat(roomObj.cost_price) || 0,
                    selling_price: parseFloat(roomObj.selling_price) || 0,
                    selling_original: parseFloat(roomObj.selling_price) || 0,
                };
            } else {
                list[index] = { ...list[index], room_category: null };
            }
            return { ...prev, rooms: list };
        });
        setIsDirty(true);
    }, [transportSupplierOptions]);

    const addRow = (listName, templateName) => {
        if (listName === 'payments') {
            setFormData(prev => ({
                ...prev,
                payments: [
                    ...(prev.payments || []), 
                    { 
                        payment_type: 'TRANSFER', 
                        amount: 0, 
                        amount_original: 0,
                        currency: 'VND',
                        rate: 1,
                        note: '', bill_image: null, supplier: null 
                    }
                ]
            }));
            return;
        }

        let newItem = { ...ITEM_TEMPLATES[templateName] };

        if (listName === 'transports' && formData.start_date) {
            newItem.pickup_datetime = `${formData.start_date}T07:00`;
        }

        if (listName === 'payables') {
            setFormData(prev => ({
                ...prev,
                payables: [
                    ...(prev.payables || []),
                    { 
                        payable_type: 'OTHER', 
                        amount: 0, 
                        amount_original: 0, 
                        currency: 'VND', 
                        exchange_rate: 1, 
                        status: 'UNPAID', 
                        note: '' 
                    }
                ]
            }));
            return;
        }

        if (listName === 'service_refunds') {
            setFormData(prev => ({
                ...prev,
                service_refunds: [
                    ...(prev.service_refunds || []),
                    { amount: 0, amount_original: 0, currency: 'VND', rate: 1, supplier: null, note: '', receiver_info: '', payment_status: 'UNPAID', bill_image: null }
                ]
            }));
            return;
        }

        setFormData(prev => ({ ...prev, [listName]: [...(prev[listName] || []), newItem] }));
    };

    const removeRow = useCallback((listName, index) => {
        setFormData(prev => ({ ...prev, [listName]: prev[listName].filter((_, i) => i !== index) }));
    }, []);

    const executeSubmit = async () => {
        if (formData.booking_status === 'CLOSED') {
            if (formData.operation_status !== 'DONE') {
                showToast("Không thể Hoàn thành: Tour chưa đi xong (Trạng thái ĐH phải là 'Đã đi xong').", "error");
                return;
            }

            const hasUnsettledCollection = (formData.payments || []).some(p => 
                p.payment_type === 'COLLECTION' && p.status !== 'SETTLED' && p.status !== 'REJECTED'
            );

            if (hasUnsettledCollection) {
                showToast("Không thể Hoàn thành: Có khoản Thu hộ chưa Quyết toán (SETTLED).", "error");
                return;
            }

            const totalPaid = (formData.payments || []).reduce((sum, p) => {
                const status = p.status || 'CONFIRMED'; 
                
                if (p.payment_type === 'COLLECTION') {
                    if (status === 'SETTLED') return sum + (parseFloat(p.amount) || 0);
                    return sum;
                }

                if (['CONFIRMED', 'SETTLED'].includes(status)) {
                    return sum + (parseFloat(p.amount) || 0);
                }
                return sum;
            }, 0);

            const totalPrice = parseFloat(formData.total_price) || 0;

            if (totalPrice > 0 && (totalPrice - totalPaid) > 1000) {
                showToast(`Không thể Hoàn thành: Khách chưa thanh toán đủ. (Đã thu: ${formatCurrency(totalPaid)} / Tổng: ${formatCurrency(totalPrice)})`, "error");
                return;
            }

            const hasUnpaidPayables = (formData.payables || []).some(p => 
                ['TOUR', 'OTHER'].includes(p.payable_type) && p.status !== 'PAID'
            );

            if (hasUnpaidPayables) {
                showToast("Không thể Hoàn thành: Công nợ Tour hoặc Khác chưa thanh toán.", "error");
                return;
            }

            const hasUnpaidItems = [
                ...formData.transports.filter(t => t.payment_method === 'SEPARATE' && t.cost_price > 0),
                ...formData.rooms.filter(r => r.payment_method === 'SEPARATE' && (r.cost_price * r.quantity) > 0),
                ...formData.extras.filter(e => e.payment_method === 'SEPARATE' && (e.cost_price * e.quantity) > 0)
            ].some(item => item.payment_status !== 'PAID');

            if (hasUnpaidItems) {
                showToast("Không thể Hoàn thành: Còn khoản công nợ (Xe/Phòng/Dịch vụ) chưa thanh toán.", "error");
                return;
            }
        }

        const custData = formData.customer || {};
        const payablesPayload = [];

        const paymentsPayload = (formData.payments || []).map(p => ({
            id: p.id,
            payment_type: p.payment_type,
            amount: p.amount,
            supplier: p.supplier ? (p.supplier.id || p.supplier) : null,
            sender_info: p.sender_info,
            receive_account: p.receive_account ? (p.receive_account.id || p.receive_account) : null,
            note: p.note,
            status: p.status
        }));

        (formData.payables || []).forEach(p => {
            if (p.payable_type !== 'REFUND') {
                let pPayload = {
                    payable_type: p.payable_type, 
                    supplier: p.supplier ? (p.supplier.id || p.supplier) : null,
                    amount: p.amount, 
                    amount_original: p.amount_original,
                    currency: p.currency, 
                    exchange_rate: p.exchange_rate,
                    note: p.note, 
                    status: p.status
                };
                if (p.id) pPayload.id = p.id;
                else if (p.payable_id) pPayload.id = p.payable_id;
                payablesPayload.push(pPayload);
            }
        });

        if (formData.refund_amount > 0 || formData.refund_payable_id || formData.refund_account_info || formData.cancellation_reason) {
            payablesPayload.push({
                id: formData.refund_payable_id,
                payable_type: 'REFUND',
                supplier: null,
                amount: formData.refund_amount,
                amount_original: formData.refund_original,
                currency: formData.refund_currency,
                exchange_rate: formData.refund_rate,
                note: formData.refund_bill_note || formData.cancellation_reason || 'Hoàn tiền',
                receiver_info: formData.refund_account_info,
                status: formData.refund_status === 'COMPLETED' ? 'PAID' : 'UNPAID'
            });
        }

        (formData.service_refunds || []).forEach(ref => {
            if (ref.amount > 0) {
                let srPayload = {
                    payable_type: 'REFUND',
                    supplier: ref.supplier ? (ref.supplier.id || ref.supplier) : null,
                    amount: ref.amount,
                    amount_original: ref.amount_original,
                    currency: ref.amount_currency || ref.currency || 'VND',
                    exchange_rate: ref.amount_rate || ref.rate || 1,
                    note: ref.note || 'Hoàn tiền dịch vụ lỗi',
                    receiver_info: ref.receiver_info || '',
                    status: ref.payment_status
                };
                if (ref.payable_id) srPayload.id = ref.payable_id;
                payablesPayload.push(srPayload);
            }
        });

        const payload = {
            ...formData,
            customer: custData.id || null,
            customer_charges: formData.customer_charges || [],
            new_customer_name: !custData.id ? custData.name : '',
            new_customer_phone: custData.phone || '',
            new_customer_contact_notes: custData.contact_notes || '',
            tour: (formData.tours && formData.tours.length > 0) ? formData.tours[0].tour : null,
            sale_owner: formData.sale_owner || null,
            guests: (formData.guests || []).map(g => {
                const guestData = { ...g, dob: g.dob || null };
                delete guestData.passport_image;
                return guestData;
            }),
            tours: (formData.tours || []).map(t => ({
                ...t,
                supplier: t.supplier ? (t.supplier.id || t.supplier) : null,
                operation_status: t.operation_status || 'OPEN',
                payment_method: 'SEPARATE'
            })),
            transports: (formData.transports || []).map(t => ({
                ...t, 
                supplier: t.supplier ? (t.supplier.id || t.supplier) : null, 
                booking_tour: t.booking_tour || null,
                operation_status: t.operation_status || 'OPEN',
                payment_method: t.payment_method || 'SEPARATE',
                pickup_datetime: (t.pickup_datetime && t.pickup_datetime !== '') ? t.pickup_datetime : null
            })),
            rooms: (formData.rooms || []).map(r => ({ 
                ...r, 
                supplier: r.supplier ? (r.supplier.id || r.supplier) : null,
                room_category: r.room_category ? (r.room_category.room_code || r.room_category) : null,
                booking_tour: r.booking_tour || null,
                operation_status: r.operation_status || 'OPEN',
                payment_method: r.payment_method || 'INCLUDED'
            })),
            extras: (formData.extras || []).map(e => ({ 
                ...e, 
                supplier: e.supplier ? (e.supplier.id || e.supplier) : null,
                service_date: (e.service_date && e.service_date !== '') ? e.service_date : null,
                booking_tour: e.booking_tour || null,
                payment_method: e.payment_method || 'SEPARATE',
                operation_status: e.operation_status || 'OPEN'
            })),
            payments: paymentsPayload, 
            payables: payablesPayload,
            cancellation_reason: formData.cancellation_reason
        };

        const customItineraryFile = formData.custom_itinerary;
        delete payload.custom_itinerary; 

        ['tour_bill_image'].forEach(k => delete payload[k]);

        try {
            const apiCall = isEditMode ? API.updateBooking(formData.booking_code, payload) : API.createBooking(payload);
            const res = await apiCall;
            const bookingId = res.data.booking_code;

            if (!isEditMode) {
                setIsEditMode(true);
                setFormData(prev => ({ ...prev, booking_code: bookingId }));
            }

            const uploadPromises = [];

            if (customItineraryFile instanceof File) {
                const fd = new FormData();
                fd.append('custom_itinerary', customItineraryFile);
                uploadPromises.push(API.client.patch(`/bookings/${bookingId}/`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                }));
            }

            const savedGuests = res.data.guests || [];
            (formData.guests || []).forEach((g, idx) => {
                if (g.passport_image instanceof File) {
                    const targetId = g.id || (savedGuests[idx] ? savedGuests[idx].id : null);
                    if (targetId) {
                        const fd = new FormData();
                        fd.append('passport_image', g.passport_image);
                        uploadPromises.push(API.client.patch(`/guests/${targetId}/`, fd, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                        }));
                    }
                }
            });

            const savedPayments = res.data.payments || [];
            (formData.payments || []).forEach((pay) => {
                if (pay.bill_image instanceof File) {
                    let targetId = pay.id;
                    if (!targetId) {
                        const potentialNew = savedPayments.find(p => !p.bill_image && parseFloat(p.amount) === parseFloat(pay.amount));
                        if (potentialNew) targetId = potentialNew.id;
                    }

                    if (targetId) {
                        const fd = new FormData();
                        fd.append('bill_image', pay.bill_image);
                        uploadPromises.push(API.updateBookingPayment(targetId, fd)); 
                    }
                }
            });

            const savedPayables = res.data.payables || [];
            
            (formData.payables || []).forEach(p => {
                if (p.bill_image instanceof File && p.supplier) {
                    const targetId = p.id || p.payable_id;
                    let target = targetId 
                        ? savedPayables.find(saved => saved.id === targetId)
                        : savedPayables.find(saved => saved.payable_type === p.payable_type && (saved.supplier === p.supplier.id || saved.supplier === p.supplier));
                    
                    if (target) {
                        const pData = new FormData();
                        pData.append('bill_image', p.bill_image);
                        uploadPromises.push(API.updatePayable(target.id, pData));
                    }
                }
            });

            (formData.transports || []).forEach(trans => {
                if (trans.bill_image instanceof File && trans.supplier) {
                    let type = 'EXTRA';
                    if (trans.direction === 'ONE_WAY_TO') type = 'TRANSPORT_TO';
                    else if (trans.direction === 'ONE_WAY_FROM') type = 'TRANSPORT_FROM';
                    else if (trans.direction === 'ROUND_TRIP') type = 'TRANSPORT_ROUND';
                    const target = savedPayables.find(p => p.payable_type === type && (p.supplier === trans.supplier.id || p.supplier === trans.supplier));
                    if (target) {
                        const tData = new FormData();
                        tData.append('bill_image', trans.bill_image);
                        uploadPromises.push(API.updatePayable(target.id, tData));
                    }
                }
            });

            let rIdx = 0;
            const roomPayablesFromServer = savedPayables.filter(p => p.payable_type === 'ROOM');
            (formData.rooms || []).forEach(room => {
                if (room.cost_price > 0) {
                    const target = roomPayablesFromServer[rIdx];
                    if (target && room.bill_image instanceof File) {
                        const rData = new FormData();
                        rData.append('bill_image', room.bill_image);
                        uploadPromises.push(API.updatePayable(target.id, rData));
                    }
                    rIdx++;
                }
            });

            let eIdx = 0;
            const extraPayablesFromServer = savedPayables.filter(p => p.payable_type === 'EXTRA');
            (formData.extras || []).forEach(extra => {
                if (extra.cost_price > 0 && extra.supplier && extra.payment_method === 'SEPARATE') {
                    const target = extraPayablesFromServer[eIdx];
                    if (target && extra.bill_image instanceof File) {
                        const eData = new FormData();
                        eData.append('bill_image', extra.bill_image);
                        uploadPromises.push(API.updatePayable(target.id, eData));
                    }
                    eIdx++;
                }
            });

            const allSavedRefunds = savedPayables.filter(p => p.payable_type === 'REFUND');
            let savedCancelRefund = null;
            let savedServiceRefunds = [];
            
            if (formData.booking_status === 'CANCELLED' || formData.refund_amount > 0 || formData.refund_payable_id) {
                savedCancelRefund = formData.refund_payable_id 
                    ? allSavedRefunds.find(p => p.id === formData.refund_payable_id) 
                    : allSavedRefunds[0];
                
                if (savedCancelRefund) {
                    savedServiceRefunds = allSavedRefunds.filter(p => p.id !== savedCancelRefund.id);
                } else {
                    savedServiceRefunds = allSavedRefunds;
                }
            } else {
                savedServiceRefunds = allSavedRefunds;
            }

            if (savedCancelRefund && formData.refund_bill_image instanceof File) {
                const rfData = new FormData();
                rfData.append('bill_image', formData.refund_bill_image);
                uploadPromises.push(API.updatePayable(savedCancelRefund.id, rfData));
            }

            (formData.service_refunds || []).forEach(ref => {
                if (ref.amount > 0 && ref.bill_image instanceof File) {
                    let target = null;
                    if (ref.payable_id) {
                        target = savedServiceRefunds.find(p => p.id === ref.payable_id);
                    } else {
                        target = savedServiceRefunds.find(p => parseFloat(p.amount) === parseFloat(ref.amount));
                    }

                    if (target) {
                        const srData = new FormData();
                        srData.append('bill_image', ref.bill_image);
                        uploadPromises.push(API.updatePayable(target.id, srData));
                    }
                }
            });

            await Promise.all(uploadPromises);
            showToast(`Lưu thành công Booking: ${bookingId}`);
            setIsDirty(false);
            const refreshRes = await API.getBookingDetail(bookingId);
            
            const providersRes = await API.fetchAllServiceProviders();
            const providerList = Array.isArray(providersRes.data) ? providersRes.data : (providersRes.data.results || []);
            setTransportSupplierOptions(providerList);

            setFormData(mapPayableToForm(refreshRes.data, providerList, tourOptions));
            setIsEditMode(true);
        } catch (err) {
            console.error(err);
            showToast("Lỗi lưu dữ liệu: " + (err.response?.data?.detail || err.message), "error");
        }
    };

    const handleSave = async () => {
        const custName = formData.customer?.name || formData.new_customer_name;
        const custPhone = formData.customer?.phone || formData.new_customer_phone;

        if (custPhone && custName) {
            try {
                const res = await API.client.get(`/customers/?search=${custPhone}`);
                const results = res.data.results || res.data;
                
                const existingCust = results.find(c => c.phone === custPhone);

                if (existingCust && existingCust.name.trim().toLowerCase() !== custName.trim().toLowerCase()) {
                    setCustConflictDialog({
                        open: true,
                        existingName: existingCust.name,
                        newName: custName,
                        phone: custPhone
                    });
                    return; // Dừng lại, chờ xác nhận
                }
            } catch (error) {
                console.error("Lỗi check khách hàng:", error);
            }
        }

        setConfirmModal({ 
            open: true, 
            title: 'Lưu dữ liệu', 
            content: 'Xác nhận lưu thông tin?', 
            action: 'SAVE', 
            confirmColor: 'primary' 
        });
    };

    const performRefresh = () => {
        setFormData(INITIAL_STATE);
        setIsEditMode(false);
        setIsDirty(false);
        setSearchKey(prev => prev + 1);
        showToast('Form đã được làm mới.');
        window.history.replaceState({}, document.title);
    };

    const handleRefreshForm = () => {
        if (isDirty) {
            setConfirmModal({ open: true, title: 'Xác nhận làm mới', content: 'Bạn có thông tin đang sửa chưa lưu. Bạn có chắc chắn muốn làm mới form không? Dữ liệu chưa lưu sẽ bị mất.', action: 'REFRESH', confirmColor: 'warning' });
        } else { performRefresh(); }
    };

    const handleConfirmAction = () => {
        if (confirmModal.action === 'REFRESH') performRefresh();
        else if (confirmModal.action === 'SAVE') executeSubmit();
        setConfirmModal({ ...confirmModal, open: false });
    };

    const handleViewBill = (item, type) => {
        let imgUrl = item.imageUrl;
        
        if (!imgUrl) {
            if (item.bill_image instanceof File) {
                imgUrl = URL.createObjectURL(item.bill_image);
            } else if (item.bill_image) {
                imgUrl = item.bill_image;
            }
        }

        const billType = item.type || type || '---';

        let supplierName = '---';

        const isCustomerBill = ['Khách', 'Deposit', 'Payment', 'Thu', 'TRANSFER', 'CASH', 'BOOKING'].some(k => String(billType).includes(k));

        if (isCustomerBill) {
            const cName = formData.customer?.name || formData.new_customer_name || 'Khách lẻ';
            supplierName = cName;
        } else {
            let rawSupplier = item.supplier;
            if (!rawSupplier) {
                if (billType === 'Công nợ Tour' || billType === 'TOUR') {
                    rawSupplier = formData.tour_debt_supplier;
                }
            }

            if (rawSupplier) {
                if (typeof rawSupplier === 'object' && rawSupplier.name) {
                    supplierName = rawSupplier.name;
                } else {
                    const found = transportSupplierOptions.find(s => s.id == rawSupplier);
                    if (found) supplierName = found.name;
                }
            }
        }

        let saleOwnerName = '---';
        if (formData.sale_owner) {
             if (typeof formData.sale_owner === 'object' && formData.sale_owner.last_name) {
                 saleOwnerName = `${formData.sale_owner.last_name} ${formData.sale_owner.first_name}`;
             } else {
                 const foundSale = saleOwnerOptions.find(u => u.id == formData.sale_owner);
                 if (foundSale) {
                     saleOwnerName = `${foundSale.last_name} ${foundSale.first_name}`;
                 }
             }
        }

        setBillModal({
            open: true,
            data: {
                type: billType,
                amount: item.amount || item.cost_price || item.net_tour_price || 0,
                collected_amount: item.collected_amount || 0,
                note: item.note || item.note_payment || item.tour_note || '',
                imageUrl: imgUrl,
                status: item.status || item.payment_status || item.payable_status_tour || '---',
                supplierName: supplierName,
                payment_account_name: item.payment_account_name,
                receive_account_name: item.receive_account_name,
                sender_info: item.sender_info,
                receiver_info: item.receiver_info,
                transaction_date: item.paid_at || item.transaction_date || item.created_at || item.tour_paid_at || null
            },
            bookingContext: {
                booking_code: formData.booking_code,
                customer: formData.customer_detail || (typeof formData.customer === 'object' ? formData.customer : { name: formData.new_customer_name }),
                start_date: formData.start_date,
                end_date: formData.end_date,
                sale_owner_name: saleOwnerName
            }
        });
    };

    const handleBillAction = (item, sectionType) => {
        if (!item) return;
        const billUrl = item.bill_image || item.image;

        let itemId = item.id;
        if (item.payable_id) {
            itemId = item.payable_id;
        }
        
        if (sectionType === 'BOOKING' && !itemId) {
             if (!formData.booking_code) {
                 showToast("Vui lòng lưu Booking để có Mã Booking trước khi đăng Bill!", "warning");
                 return;
             }
             itemId = formData.booking_code;
        }

        // BẮT LỖI DÀNH CHO PAYMENT CHƯA LƯU (Khi đăng bill cho một hàng thu tiền vừa tạo mới)
        if (sectionType === 'PAYMENT' && !itemId) {
             showToast("Vui lòng lưu Booking để tạo mã Giao dịch trước khi đăng Bill!", "warning");
             return;
        }

        // CẬP NHẬT LOGIC ITEM TYPE: Bắt đúng luồng BOOKING_PAYMENT cho "Khách thanh toán"
        let itemType = item.payable_type || item.type;
        if (!itemType) {
            if (sectionType === 'BOOKING' || sectionType === 'PAYMENT') itemType = 'BOOKING_PAYMENT';
            else itemType = 'PAYABLE';
        }
        
        if (!item.payable_type && sectionType === 'PAYABLE') {
            if (item.direction === 'ONE_WAY_TO') itemType = 'TRANSPORT_TO';
            else if (item.direction === 'ONE_WAY_FROM') itemType = 'TRANSPORT_FROM';
            else if (item.direction === 'ROUND_TRIP') itemType = 'TRANSPORT_ROUND';
            else if (item.room_type) itemType = 'ROOM';
            else if (item.title) itemType = 'EXTRA';
        }

        const finalAmount = item.amount || item.cost_price || item.net_tour_price || item.total_price || item.price || 0;

        // CẬP NHẬT LABEL HIỂN THỊ
        let displayType = (sectionType === 'BOOKING' || sectionType === 'PAYMENT') ? 'KHÁCH THANH TOÁN' : 'CÔNG NỢ NCC';
        let displaySupplier = item.supplierName || item.supplier_name || ((sectionType === 'BOOKING' || sectionType === 'PAYMENT') ? formData.customer?.name : item.supplier?.name);
        let displayNote = item.note || item.description;

        if (item.payment_type === 'COLLECTION') {
            displayType = 'THU HỘ (NCC)';
            displaySupplier = item.supplier ? (item.supplier.name || item.supplier) : '---';
            
            if (!displayNote) {
                displayNote = "(Bill NCC chuyển trả tiền thừa HOẶC Chứng từ cấn trừ công nợ)";
            }
        }

        if (billUrl) {
            setBillModal({
                open: true,
                data: {
                    ...item,
                    imageUrl: billUrl,
                    amount: finalAmount,
                    note: displayNote,
                    type: displayType,
                    supplierName: displaySupplier,
                    status: 'PAID',
                    transaction_date: item.paid_at || item.transaction_date || item.created_at || item.tour_paid_at || null 
                },
                bookingContext: formData 
            });
        } else {
            // SỬA: Phân loại đúng dialogType thành BOOKING (Tránh bị đẩy sang PAYABLE)
            const dialogType = (item.payment_type === 'COLLECTION') ? 'COLLECTION' : ((sectionType === 'BOOKING' || sectionType === 'PAYMENT') ? 'BOOKING' : 'PAYABLE');
            
            const bankInfo = item.supplier?.bank_info || '';
            const custName = formData.customer?.name || formData.new_customer_name;
            const custSource = formData.source;

            setBulkPayModal({
                open: true,
                items: [{
                    ...item,
                    id: itemId,
                    payable_type: itemType,
                    remain_amount: finalAmount,
                    amount: finalAmount,
                    supplier_name: displaySupplier,
                    payment_type: item.payment_type,
                    bank_info: bankInfo,
                    customer_name: custName,
                    source: custSource,
                    sender_info: item.sender_info,
                    receiver_info: item.receiver_info,
                    note: (!item.id && (sectionType === 'BOOKING' || sectionType === 'PAYMENT')) ? item.note : displayNote 
                }], 
                type: dialogType,
                onSuccess: () => {
                    loadBooking(formData.booking_code);
                }
            });
        }
    };

    const renderBillControl = (item, sectionType) => {
        if (!item) return null; 
        
        const hasBill = !!(item.bill_image || item.image || item.bill_url);

        return (
            <Button
                variant="outlined"
                fullWidth    
                size="small"
                onClick={() => handleBillAction(item, sectionType)}
                startIcon={hasBill ? <VisibilityIcon /> : <CloudUploadIcon />}
                sx={{ 
                    height: '40px',     
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    
                    borderColor: hasBill ? '#2e7d32' : 'rgba(0, 0, 0, 0.23)', 
                    color: hasBill ? '#2e7d32' : 'rgba(0, 0, 0, 0.6)',
                    fontWeight: hasBill ? 'bold' : 'normal',
                    
                    '&:hover': {
                        borderColor: hasBill ? '#1b5e20' : 'rgba(0, 0, 0, 0.87)',
                        backgroundColor: hasBill ? 'rgba(46, 125, 50, 0.04)' : 'rgba(0, 0, 0, 0.04)'
                    }
                }}
            >
                {hasBill ? "Đã có Bill" : "Đăng Bill"}
            </Button>
        );
    };
    
    const handleConfirmCancel = async (cancelData) => {
        try {
            const payload = {
                booking_status: 'CANCELLED',
                cancellation_data: isCancelled ? {
                    cancellation_type: formData.cancellation_type,
                    cancellation_reason: formData.cancellation_reason,
                    refund_amount: formData.refund_amount,
                    refund_account_info: formData.refund_account_info
                } : cancelData,
            };

            await API.updateBooking(formData.booking_code, payload);

            if (cancelData.refund_bill_image instanceof File) {
                 const res = await API.getBookingDetail(formData.booking_code);
                 const newPayables = res.data.payables || [];
                 const refundP = newPayables.find(p => p.payable_type === 'REFUND');
                 
                 if (refundP) {
                     const fd = new FormData();
                     fd.append('bill_image', cancelData.refund_bill_image);
                     fd.append('status', 'PAID');
                     await API.updatePayable(refundP.id, fd);
                 }
            }

            showToast(`Đã hủy Booking ${formData.booking_code}`, "success");
            setCancelDialogOpen(false);
            loadBooking(formData.booking_code);
        } catch (err) {
            console.error(err);
            showToast("Lỗi khi hủy booking", "error");
        }
    };

    const handleUpdateRefund = async (file) => {
        if (!file) return;
        
        if (!formData.refund_payable_id) {
            showToast("Không tìm thấy thông tin công nợ hoàn tiền. Vui lòng reload.", "error");
            return;
        }

        try {
            const fd = new FormData();
            fd.append('bill_image', file);
            fd.append('status', 'PAID'); // Có bill coi như đã trả
            
            await API.updatePayable(formData.refund_payable_id, fd);
            
            showToast("Đã cập nhật Bill hoàn tiền thành công!", "success");
            
            const res = await API.getBookingDetail(formData.booking_code);
            const updatedForm = mapPayableToForm(res.data, transportSupplierOptions, tourOptions);
            setFormData(updatedForm);
            
        } catch (err) { 
            console.error(err);
            showToast("Lỗi cập nhật hoàn tiền", "error"); 
        }
    };

    const handleOpenRefundPayment = () => {
        if (!formData.refund_amount || formData.refund_amount <= 0) {
            showToast("Vui lòng nhập số tiền cần hoàn trước!", "error");
            return;
        }
        
        if (!formData.refund_payable_id) {
            showToast("Chưa có công nợ hoàn tiền. Vui lòng lưu Hủy trước.", "warning");
            return;
        }

        setBulkPayModal({
            open: true,
            type: 'PAYABLE',
            items: [{
                id: formData.refund_payable_id, // ID Payable
                payable_type: 'REFUND',
                amount: formData.refund_amount,
                remain_amount: formData.refund_amount,
                
                supplier_name: formData.customer?.name || formData.new_customer_name, 
                receiver_info: formData.refund_account_info,
                
                note: formData.cancellation_reason || `Hoàn tiền booking ${formData.booking_code}`
            }],
            onSuccess: async () => {
                showToast("Đã cập nhật hoàn tiền thành công!", "success");
                loadBooking(formData.booking_code);
            }
        });
    };

    const handleViewRefundBill = () => {
        let imgUrl = formData.refund_bill_image;
        const refundPayable = formData.payables?.find(p => p.payable_type === 'REFUND');

        if (imgUrl instanceof File) {
            imgUrl = URL.createObjectURL(imgUrl);
        }
        
        let saleOwnerName = '---';
        if (formData.sale_owner) {
             if (typeof formData.sale_owner === 'object' && formData.sale_owner.last_name) {
                 saleOwnerName = `${formData.sale_owner.last_name} ${formData.sale_owner.first_name}`;
             } else {
                 const foundSale = saleOwnerOptions.find(u => u.id == formData.sale_owner);
                 if (foundSale) saleOwnerName = `${foundSale.last_name} ${foundSale.first_name}`;
             }
        }

        setBillModal({
            open: true,
            data: {
                type: 'HOÀN TIỀN (REFUND)',
                amount: formData.refund_amount,
                note: formData.refund_bill_note || formData.cancellation_reason || '---',
                imageUrl: imgUrl,
                status: 'PAID',
                supplierName: formData.customer?.name || formData.new_customer_name,
                receiver_info: formData.refund_account_info,
                payment_account_name: refundPayable?.payment_account_name || 'Chưa xác định',
                transaction_date: refundPayable?.paid_at || null
            },
            bookingContext: {
                booking_code: formData.booking_code,
                customer: formData.customer || { name: formData.new_customer_name },
                start_date: formData.start_date,
                end_date: formData.end_date,
                sale_owner_name: saleOwnerName
            }
        });
    };

    const handleViewPassport = (guest) => {
        let imgUrl = guest.passport_image;
        
        if (imgUrl instanceof File) {
            imgUrl = URL.createObjectURL(imgUrl);
        }
        
        setPassportPreview({ open: true, url: imgUrl });
    };

    const isLockedByDone = formData.operation_status === 'DONE'; 
    const isClosed = formData.booking_status === 'CLOSED';       
    const isCancelled = formData.booking_status === 'CANCELLED';

    const isRefundSettled = isCancelled && (
        ['NO_PAYMENT', 'NO_REFUND'].includes(formData.cancellation_type) ||
        (formData.cancellation_type === 'REFUND' && formData.refund_status === 'COMPLETED')
    );
    const shouldDisableSave = (isClosed || isRefundSettled) && !isDirty;

    const isInfoLocked = isLockedByDone || isClosed || isCancelled;
    const isFinanceLocked = isClosed || isCancelled; 
    
    const totalGuests = (formData.adults_count || 0) + (formData.children_count || 0);

    const calcTotalLiability = (formData.payables || [])
        .filter(p => p.payable_type !== 'REFUND')
        .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        
    const calcPaidLiability = (formData.payables || [])
        .filter(p => p.payable_type !== 'REFUND' && p.status === 'PAID')
        .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        
    const calcRemainingLiability = calcTotalLiability - calcPaidLiability;

    return (
        <ThemeProvider theme={formTheme}>
            <Container maxWidth={false} sx={{ maxWidth: '1400px', mt: 3, mb: 3 }}>
                {/* --- HEADER KHU VỰC --- */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#007373' }}>
                        FORM NHẬP/ SỬA BOOKING
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {isEditMode &&
                            <>
                                <Button variant="contained" startIcon={<ReceiptIcon />} onClick={() => setTicketDialogOpen(true)} sx={{ bgcolor: '#007373', '&:hover': { bgcolor: '#005c5c' }, textTransform: 'none', fontWeight: 'bold' }}>Xuất E-ticket</Button>
                                <Button variant="contained" startIcon={<DescriptionIcon />} onClick={() => setInvoiceDialogOpen(true)} sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, textTransform: 'none', fontWeight: 'bold' }}>Xuất Invoice</Button>
                            </>
                        }
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefreshForm} sx={{ bgcolor: 'white' }}>Làm mới</Button>
                    </Box>
                </Box>

                <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                    <FormSection title="" collapsible={false}>
                        <FastSearchSection 
                            key={searchKey}
                            onSelectCustomer={(cust) => { setFormData(prev => ({ ...prev, customer: cust })); setIsEditMode(false); }}
                            onSelectBooking={async (bk) => {
                                if (bk && (bk.isNew || typeof bk === 'string')) {
                                    const newCode = bk.code || bk; 
                                    setFormData(prev => ({
                                        ...INITIAL_STATE,
                                        booking_code: newCode,
                                        customer: prev.customer
                                    }));
                                    setIsEditMode(false);
                                    showToast(`Đang tạo Booking mới với mã: ${newCode}`, "info");
                                } else if (bk && bk.booking_code) {
                                    try {
                                        const res = await API.getBookingDetail(bk.booking_code);
                                        setFormData(mapPayableToForm(res.data, transportSupplierOptions));
                                        setIsEditMode(true);
                                        showToast(`Đã mở Booking: ${bk.booking_code}`);
                                    } catch (e) { showToast("Lỗi tải booking", "error"); }
                                }
                            }}
                            currentCustomer={formData.customer}
                            currentBookingCode={formData.booking_code}
                            isLocked={isInfoLocked}
                        />
                    </FormSection>

                    {isClosed && <Alert severity="warning" variant="filled" sx={{ mb: 3, fontWeight: 'bold' }}>BOOKING ĐÃ HOÀN THÀNH VÀ BỊ KHÓA TOÀN BỘ.</Alert>}

                    {formData.booking_status === 'CANCELLED' && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: '#fff5f5', border: '1px solid #ffcdd2', borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#d32f2f', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DeleteIcon /> THÔNG TIN HỦY & HOÀN TIỀN
                            </Typography>
                            
                            {(() => {
                                const cancelType = formData.cancellation_type;
                                // Xác định nhóm không hoàn tiền
                                const isNoRefund = ['NO_PAYMENT', 'NO_REFUND'].includes(cancelType);
                                
                                // Map text tiếng Việt hiển thị
                                const typeLabel = {
                                    'NO_PAYMENT': 'Chưa thanh toán (Không hoàn tiền)',
                                    'REFUND': 'Đã thanh toán - Có hoàn tiền',
                                    'NO_REFUND': 'Đã thanh toán - Không hoàn tiền (Phạt 100%)'
                                }[cancelType] || 'Không xác định';

                                // TRƯỜNG HỢP 1: KHÔNG CẦN HOÀN TIỀN (Chỉ hiện Thông báo + Lý do)
                                if (isNoRefund) {
                                    return (
                                        <Box>
                                            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#ffebee', borderRadius: 1, borderLeft: '4px solid #d32f2f' }}>
                                                <Typography variant="body1" sx={{ color: '#c62828' }}>
                                                    Loại hủy: <b>{typeLabel}</b>
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#555' }}>
                                                    Booking này không phát sinh giao dịch hoàn tiền.
                                                </Typography>
                                            </Box>
                                            
                                            <CustomLabel title="Lý do hủy" />
                                            <TextField 
                                                size="small" fullWidth multiline minRows={1}
                                                value={formData.cancellation_reason || ''} 
                                                onChange={(e) => {
                                                    setFormData(prev => ({ ...prev, cancellation_reason: e.target.value }));
                                                    setIsDirty(true);
                                                }}
                                                placeholder="Nhập lý do hủy..."
                                                sx={{ bgcolor: 'white' }}
                                            />
                                        </Box>
                                    );
                                }

                                // TRƯỜNG HỢP 2: CÓ HOÀN TIỀN (Hiện đầy đủ UI cũ)
                                return (
                                <>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ color: '#d32f2f', fontStyle: 'italic' }}>
                                            Loại hủy: <b>{typeLabel}</b>
                                        </Typography>
                                    </Box>

                                    {/* Dòng 1: Lý do & Số tiền hoàn */}
                                    <Box sx={rowStyle}>
                                        <Box flex={2}>
                                            <CustomLabel title="Lý do hủy" />
                                            <TextField 
                                                size="small" fullWidth 
                                                value={formData.cancellation_reason || ''} 
                                                onChange={(e) => {
                                                    setFormData(prev => ({ ...prev, cancellation_reason: e.target.value }));
                                                    setIsDirty(true);
                                                }}
                                                disabled={formData.refund_status === 'COMPLETED'} 
                                                placeholder="Nhập lý do hủy..."
                                                sx={{ bgcolor: 'white' }}
                                            />
                                        </Box>
                                        <Box flex={1}>
                                            <CustomLabel title="Số tiền cần hoàn" />
                                            <CurrencyInput
                                                value={{
                                                    original: formData.refund_original ?? formData.refund_amount,
                                                    currency: formData.refund_currency || 'VND',
                                                    rate: formData.refund_rate || 1,
                                                    vnd: formData.refund_amount
                                                }}
                                                disabled={formData.refund_status === 'COMPLETED'}
                                                onChange={(obj) => {
                                                    setFormData(prev => ({ 
                                                        ...prev, 
                                                        refund_amount: obj.vnd,
                                                        refund_original: obj.original,
                                                        refund_currency: obj.currency,
                                                        refund_rate: obj.rate
                                                    }));
                                                    setIsDirty(true);
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Dòng 2: Thông tin TK & Nút Upload/Xem Bill */}
                                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                                        <Box flex={2}>
                                            <CustomLabel title="Thông tin TK nhận tiền (Của khách)" />
                                            <TextField 
                                                size="small" fullWidth multiline minRows={1}
                                                placeholder="VD: 1903... - Techcombank - Nguyen Van A"
                                                value={formData.refund_account_info || ''}
                                                onChange={(e) => {
                                                    setFormData(prev => ({ ...prev, refund_account_info: e.target.value }));
                                                    setIsDirty(true);
                                                }}
                                                disabled={formData.refund_status === 'COMPLETED'}
                                                sx={{ bgcolor: 'white', marginBottom: '0px !important' }}
                                            />
                                        </Box>
                                        
                                        <Box flex={1} sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                                            {formData.refund_status !== 'COMPLETED' ? (
                                                <Button 
                                                    variant="contained" 
                                                    color="warning" 
                                                    fullWidth
                                                    sx={{ height: '40px', fontWeight: 'bold' }}
                                                    startIcon={<PaymentIcon />} 
                                                    disabled={!formData.refund_amount || formData.refund_amount <= 0}
                                                    onClick={handleOpenRefundPayment} 
                                                >
                                                    Thanh toán Hoàn tiền
                                                </Button>
                                            ) : (
                                                <Box sx={{ width: '100%', display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    <Chip label="ĐÃ HOÀN TIỀN" color="success" sx={{ fontWeight: 'bold', height: '40px', flex: 1 }} />
                                                    {formData.refund_bill_image && (
                                                        <Button 
                                                            variant="outlined" 
                                                            color="primary" 
                                                            onClick={handleViewRefundBill} 
                                                            sx={{ height: '40px', bgcolor: 'white' }}
                                                        >
                                                            Xem Bill
                                                        </Button>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </>
                                );
                            })()}
                        </Box>
                    )}

                    <FormSection title="THÔNG TIN KHÁCH HÀNG" defaultExpanded={true}>
                        <Box sx={rowStyle}>
                            <Box flex={1}><CustomLabel title="Tên khách hàng" required /><TextField size="small" fullWidth name="name" value={formData.customer?.name || ''} disabled={isInfoLocked} onChange={(e) => setFormData(p => ({...p, customer: {...p.customer, name: e.target.value}}))} /></Box>
                            <Box flex={1}><CustomLabel title="SĐT" required /><TextField size="small" fullWidth name="phone" value={formData.customer?.phone || ''} disabled={isInfoLocked} onChange={(e) => setFormData(p => ({...p, customer: {...p.customer, phone: e.target.value}}))} /></Box>
                            <Box flex={1}><CustomLabel title="Ghi chú liên hệ" /><TextField size="small" fullWidth name="contact_notes" value={formData.customer?.contact_notes || ''} disabled={isInfoLocked} onChange={(e) => setFormData(p => ({...p, customer: {...p.customer, contact_notes: e.target.value}}))} /></Box>
                        </Box>
                        <Box sx={rowStyle}>
                            <Box flex={1}><CustomLabel title="NV Phụ trách" required /><TextField select fullWidth size="small" name="sale_owner" value={formData.sale_owner || ''} disabled={isInfoLocked} onChange={handleChange}>{saleOwnerOptions.map(u => <MenuItem key={u.id} value={u.id}>{u.last_name} {u.first_name}</MenuItem>)}</TextField></Box>
                            <Box flex={1}><CustomLabel title="Nguồn khách" required /><TextField select fullWidth size="small" name="source" value={formData.source || ''} disabled={isInfoLocked} onChange={handleChange}>{['Website', 'Facebook', 'Zalo', 'Klook', 'Viator', 'Direct'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}</TextField></Box>
                            <Box flex={1}>
                                <CustomLabel title="Nhu cầu" required />
                                <TextField select fullWidth size="small" name="customer_demand" value={formData.customer_demand} disabled={isInfoLocked} onChange={handleChange}>
                                    <MenuItem value="LE">Khách lẻ</MenuItem><MenuItem value="DOAN">Khách đoàn</MenuItem><MenuItem value="XE">Khách thuê xe</MenuItem><MenuItem value="KHAC">Khác</MenuItem>
                                </TextField>
                            </Box>
                        </Box>
                        <Box sx={{ mb: 1 }}><CustomLabel title="Yêu cầu đặc biệt" /><TextField name="special_request" size="small" fullWidth multiline value={formData.special_request} disabled={isInfoLocked} onChange={handleChange} /></Box>
                        <Box sx={rowStyle}>
                            <Box flex={1}><CustomLabel title="Tổng số khách" /><TextField size="small" value={totalGuests} fullWidth disabled /></Box>
                            <Box flex={1}><CustomLabel title="Số người lớn" /><TextField name="adults_count" size="small" fullWidth type="number" value={formData.adults_count} disabled={isInfoLocked} onChange={handleChange} /></Box>
                            <Box flex={1}><CustomLabel title="Số trẻ em" /><TextField name="children_count" size="small" fullWidth type="number" value={formData.children_count} disabled={isInfoLocked} onChange={handleChange} /></Box>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Danh sách đoàn</Typography>
                        {formData.guests.map((g, i) => (
                            <GuestItem 
                                key={i} 
                                item={g} 
                                index={i} 
                                isLocked={isInfoLocked} 
                                onChange={handleListChange} 
                                onRemove={removeRow} 
                                onViewPassport={handleViewPassport} 
                            />
                        ))}
                        <Button startIcon={<AddIcon />} onClick={() => addRow('guests', 'guest')} disabled={isInfoLocked} variant="outlined" size="small">Thêm khách</Button>
                    </FormSection>

                    <FormSection title="THÔNG TIN TOUR & DỊCH VỤ">    
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#007373' }}>Danh sách Tour / Lịch trình</Typography>
                        {(formData.tours || []).map((t, i) => (
                            <TourItem key={i} item={t} index={i} isLocked={isInfoLocked} tourOptions={tourOptions} supplierOptions={transportSupplierOptions} 
                                onTourSelect={handleTourSelect} 
                                onChange={handleListChange} onRemove={removeRow} 
                            />
                        ))}
                        <Button startIcon={<AddIcon />} onClick={() => addRow('tours', 'tour')} disabled={isInfoLocked} variant="outlined" size="small" sx={{ mb: 2 }}>Thêm Tour</Button>

                        {/* THÊM MỚI: HIỂN THỊ VÀ UPLOAD LỊCH TRÌNH */}
                        <Box sx={{ ...rowStyle, mt: 1, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px dashed #ced4da' }}>
                            <Box flex={1}>
                                <CustomLabel title="Lịch trình Tour (Mặc định)" />
                                {(() => {
                                    // Tìm kiếm file lịch trình từ TẤT CẢ các mã tour đã chọn trong danh sách mảng tours
                                    const toursWithItinerary = (formData.tours || [])
                                        .map(t => tourOptions.find(opt => opt.tour_code === t.tour))
                                        .filter(t => t && t.itinerary); // Chỉ lấy những tour có cấu hình file itinerary

                                    if (toursWithItinerary.length > 0) {
                                        return (
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                                                {toursWithItinerary.map((t, idx) => (
                                                    <Button 
                                                        key={idx}
                                                        href={t.itinerary} 
                                                        target="_blank" 
                                                        variant="contained" 
                                                        color="info" 
                                                        size="small"
                                                        sx={{ textTransform: 'none', fontWeight: 'bold', mb: 0.5 }}
                                                    >
                                                        {`Xem LT: ${t.name || t.tour_code}`}
                                                    </Button>
                                                ))}
                                            </Box>
                                        );
                                    }

                                    return (
                                        <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic', pt: 1 }}>
                                            Chưa có lịch trình mặc định từ các tour đã chọn.
                                        </Typography>
                                    );
                                })()}
                            </Box>
                            
                            <Box flex={1}>
                                <CustomLabel title="Lịch trình riêng (Dành riêng cho Booking này)" />
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Button 
                                        variant="outlined" 
                                        component="label" 
                                        size="small" 
                                        startIcon={<CloudUploadIcon />}
                                        disabled={isInfoLocked}
                                    >
                                        Tải lên LT Riêng
                                        <input 
                                            type="file" 
                                            hidden 
                                            accept=".pdf,.doc,.docx,image/*" 
                                            onChange={e => {
                                                if(e.target.files[0]) {
                                                    setFormData(p => ({...p, custom_itinerary: e.target.files[0]}));
                                                    setIsDirty(true);
                                                }
                                            }} 
                                        />
                                    </Button>
                                    {formData.custom_itinerary && (
                                        <Button
                                            size="small"
                                            color="success"
                                            variant="contained"
                                            sx={{ textTransform: 'none', fontWeight: 'bold' }}
                                            href={formData.custom_itinerary instanceof File ? URL.createObjectURL(formData.custom_itinerary) : formData.custom_itinerary}
                                            target="_blank"
                                        >
                                            Xem LT Riêng
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* --- NẾU LÀ TRUNG GIAN THÌ HIỂN THỊ Ô NHẬP GHI CHÚ VÀ CẢNH BÁO --- */}
                        {formData.booking_type === 'INTERMEDIARY' && (
                            <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', border: '1px dashed #ff9800', borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ color: '#e65100', fontWeight: 'bold', mb: 1 }}>
                                    ⚠️ ĐÂY LÀ BOOKING TRUNG GIAN / GỬI KHÁCH
                                </Typography>
                                <CustomLabel title="Chi tiết/ Thỏa thuận trung gian" />
                                <TextField 
                                    name="intermediary_note" 
                                    size="small" 
                                    fullWidth 
                                    multiline 
                                    minRows={1} 
                                    value={formData.intermediary_note || ''} 
                                    disabled={isFinanceLocked} 
                                    onChange={handleChange}
                                    sx={{ bgcolor: 'white' }} 
                                />
                            </Box>
                        )}

                        {/* --- CHI TIẾT HẠNG PHÒNG (Luôn hiển thị) --- */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, mt: 2, color: '#007373' }}>Chi tiết Hạng phòng KS</Typography>
                            {(formData.rooms || []).map((r, i) => (
                                <RoomItem key={i} item={r} index={i} isLocked={isInfoLocked} supplierOptions={transportSupplierOptions} roomOptions={hotelRoomOptions} toursList={formData.tours} onRoomSelect={handleRoomCategorySelect} onChange={handleListChange} onRemove={removeRow} />
                            ))}
                            <Button startIcon={<AddIcon />} onClick={() => addRow('rooms', 'room')} disabled={isInfoLocked} variant="outlined" size="small">Thêm phòng</Button>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* --- HÀNG 2: NGÀY THÁNG VÀ TRẠNG THÁI --- */}
                        <Box sx={rowStyle}>
                            <Box flex={1}><CustomLabel title="Trạng thái BK" required /><TextField select fullWidth size="small" name="booking_status" value={formData.booking_status} onChange={handleChange} disabled={formData.booking_status === 'CANCELLED'}>{BOOKING_STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}</TextField></Box>
                            <Box flex={1}><CustomLabel title="Trạng thái ĐH" /><TextField select fullWidth size="small" name="operation_status" value={formData.operation_status} onChange={handleChange} disabled={isFinanceLocked}>{OPS_STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}</TextField></Box>
                            <Box flex={1}><CustomLabel title="Ngày đi" required /><CustomDatePicker name="start_date" value={formData.start_date} onChange={handleChange} disabled={isInfoLocked} required /></Box>
                            <Box flex={0.8}><CustomLabel title="Số ngày" /><TextField type="number" size="small" name="duration_days_actual" value={formData.duration_days_actual || (formData.tour?.duration_days || 1)} onChange={handleChange} disabled={isInfoLocked} fullWidth /></Box>
                            <Box flex={1}><CustomLabel title="Ngày về" /><TextField size="small" value={formatDateDisplay(formData.end_date)} disabled InputProps={{ readOnly: true }} fullWidth /></Box>
                        </Box>

                        <Box sx={{ mb: 1, mt: 2 }}><CustomLabel title="Ghi chú ĐH" /><TextField name="operator_notes" size="small" fullWidth value={formData.operator_notes} disabled={isInfoLocked} onChange={handleChange} multiline /></Box>
                        
                        {/* --- FEEDBACK --- */}
                        {formData.operation_status === 'DONE' && (
                            <Box>
                                <CustomLabel title="Feedback Khách hàng sau khi đi" />
                                <TextField name="customer_feedback" size="small" fullWidth multiline minRows={1} value={formData.customer_feedback || ''} disabled={isFinanceLocked} onChange={handleChange} placeholder="Nhập phản hồi của khách hàng..." sx={{ bgcolor: 'white' }} />
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* --- DỊCH VỤ PHÁT SINH --- */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Dịch vụ phát sinh</Typography>
                        {formData.extras.map((ex, i) => (
                            <ExtraItem key={i} item={ex} index={i} isLocked={isFinanceLocked || ex.payment_status === 'PAID'} supplierOptions={transportSupplierOptions} toursList={formData.tours} onChange={handleListChange} onMoneyChange={handleMoneyChange} onRemove={removeRow} />
                        ))}
                        <Button startIcon={<AddIcon />} onClick={() => addRow('extras', 'extra')} disabled={isFinanceLocked} variant="outlined" size="small">Thêm dịch vụ</Button>
                        
                        <Divider sx={{ my: 2 }} />

                        {/* --- THÔNG TIN ĐIỀU XE --- */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Thông tin điều xe</Typography>
                        {formData.transports.map((t, i) => (
                            <TransportItem 
                                key={i} item={t} index={i} 
                                isLocked={isInfoLocked || t.payment_status === 'PAID'} 
                                supplierOptions={transportSupplierOptions} 
                                routeOptions={transportRouteOptions}
                                toursList={formData.tours}
                                onRouteSelect={handleTransportRouteSelect} 
                                onChange={handleListChange} 
                                onRemove={removeRow} 
                            />
                        ))}
                        <Button startIcon={<AddIcon />} onClick={() => addRow('transports', 'transport')} disabled={isInfoLocked} variant="outlined" size="small">Thêm điều xe</Button>
                    </FormSection>

                    {/* =========================================================
                                PHẦN 1: THANH TOÁN CỦA KHÁCH HÀNG 
                    ========================================================== */}
                    <FormSection title="THÔNG TIN THANH TOÁN (KHÁCH HÀNG)">
                        
                        {/* 1. Mảng Động: Các mục tính tiền khách hàng */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#007373' }}>
                            Các hạng mục thu tiền
                        </Typography>
                        {(formData.customer_charges || []).map((charge, i) => (
                            <CustomerChargeItem 
                                key={i} 
                                item={charge} 
                                index={i} 
                                isLocked={isFinanceLocked} 
                                onChange={handleListChange} 
                                onMoneyChange={handleMoneyChange} 
                                onRemove={removeRow} 
                            />
                        ))}
                        <Button 
                            startIcon={<AddIcon />} 
                            onClick={() => addRow('customer_charges')} 
                            disabled={isFinanceLocked} 
                            variant="outlined" size="small" sx={{ mb: 3 }}
                        >
                            Thêm mục thu tiền
                        </Button>

                        <Divider sx={{ mb: 2 }} />

                        {/* 2. Box Tổng hợp tiền */}
                        <Box sx={{ ...rowStyle, bgcolor: '#f5f5f5', p: 2, borderRadius: 2, mb: 3 }}>
                            <Box flex={1.5}>
                                <CustomLabel title="Tổng giá Booking" />
                                <CurrencyInput 
                                    value={{ 
                                        original: formData.original_total_price, 
                                        currency: formData.currency, 
                                        rate: formData.exchange_rate, 
                                        vnd: formData.total_price 
                                    }} 
                                    disabled={isFinanceLocked} 
                                    onChange={handleTotalPriceChange} 
                                />
                            </Box>
                            <Box flex={1}>
                                <CustomLabel title="Khách đã trả" />
                                <TextField 
                                    size="small" fullWidth disabled 
                                    value={new Intl.NumberFormat('vi-VN').format(formData.amount_received || 0)} 
                                    sx={{ '& input': { fontWeight: 'bold', color: '#2e7d32' } }} 
                                />
                            </Box>
                            <Box flex={1}>
                                <CustomLabel title="Còn phải thu" />
                                <TextField 
                                    size="small" fullWidth disabled 
                                    value={new Intl.NumberFormat('vi-VN').format(Math.max(0, (formData.total_price || 0) - (formData.amount_received || 0)))} 
                                    sx={{ '& input': { fontWeight: 'bold', color: '#d32f2f' } }} 
                                />
                            </Box>
                            <Box flex={1}>
                                <CustomLabel title="Trạng thái Thanh toán" />
                                <TextField 
                                    select fullWidth size="small" 
                                    value={formData.payment_status || 'UNPAID'} 
                                    disabled={isFinanceLocked} 
                                    onChange={(e) => setFormData(prev => ({ ...prev, payment_status: e.target.value }))}
                                >
                                    <MenuItem value="UNPAID">Chưa TT</MenuItem>
                                    <MenuItem value="DEPOSIT">Đã cọc</MenuItem>
                                    <MenuItem value="PAID">Đã TT</MenuItem>
                                </TextField>
                            </Box>
                        </Box>

                        {/* 3. Lịch sử Giao dịch thanh toán của khách */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Lịch sử khách thanh toán</Typography>
                        {(formData.payments || []).map((payment, i) => (
                            <PaymentItem 
                                key={i} 
                                item={payment} 
                                index={i} 
                                isLocked={isFinanceLocked || payment.status === 'SETTLED'} 
                                accountOptions={financialAccounts} 
                                supplierOptions={transportSupplierOptions} 
                                onChange={handleListChange} 
                                onMoneyChange={handleMoneyChange} 
                                onRemove={removeRow} 
                                onBillAction={() => handleBillAction(payment, 'PAYMENT')} 
                            />
                        ))}
                        <Button 
                            startIcon={<AddIcon />} 
                            onClick={() => addRow('payments')} 
                            disabled={isFinanceLocked} 
                            variant="outlined" size="small"
                        >
                            Thêm GD thanh toán
                        </Button>

                    </FormSection>


                    {/* =========================================================
                                PHẦN 2: QUẢN LÝ CÔNG NỢ NHÀ CUNG CẤP 
                    ========================================================== */}
                    <FormSection title="QUẢN LÝ CÔNG NỢ (NHÀ CUNG CẤP)">
                        
                        {/* --- CẬP NHẬT: BOX TỔNG HỢP CÔNG NỢ NỀN TRẮNG, CHỮ MÀU RÕ RÀNG KHÔNG BỊ MỜ --- */}
                        <Box sx={{ ...rowStyle, bgcolor: '#f5f5f5', p: 2, borderRadius: 2, mb: 3 }}>
                            <Box flex={1}>
                                <CustomLabel title="Tổng Công nợ NCC" />
                                <TextField 
                                    size="small" fullWidth disabled 
                                    value={new Intl.NumberFormat('vi-VN').format(calcTotalLiability)} 
                                    sx={{
                                        '& .MuiOutlinedInput-root.Mui-disabled': {
                                            backgroundColor: '#fff', // Giữ nền trắng tuyệt đối
                                        },
                                        '& .MuiOutlinedInput-input.Mui-disabled': {
                                            fontWeight: 'bold',
                                            color: '#1e3d59', // Màu xanh đen chính thể
                                            WebkitTextFillColor: '#1e3d59', // Ép trình duyệt hiển thị đúng màu khi disabled
                                        }
                                    }} 
                                />
                            </Box>
                            <Box flex={1}>
                                <CustomLabel title="Đã thanh toán" />
                                <TextField 
                                    size="small" fullWidth disabled 
                                    value={new Intl.NumberFormat('vi-VN').format(calcPaidLiability)} 
                                    sx={{
                                        '& .MuiOutlinedInput-root.Mui-disabled': {
                                            backgroundColor: '#fff',
                                        },
                                        '& .MuiOutlinedInput-input.Mui-disabled': {
                                            fontWeight: 'bold',
                                            color: '#2e7d32', // Màu xanh lá cây đậm
                                            WebkitTextFillColor: '#2e7d32',
                                        }
                                    }} 
                                />
                            </Box>
                            <Box flex={1}>
                                <CustomLabel title="Còn lại (Chưa trả)" />
                                <TextField 
                                    size="small" fullWidth disabled 
                                    value={new Intl.NumberFormat('vi-VN').format(calcRemainingLiability)} 
                                    sx={{
                                        '& .MuiOutlinedInput-root.Mui-disabled': {
                                            backgroundColor: '#fff',
                                        },
                                        '& .MuiOutlinedInput-input.Mui-disabled': {
                                            fontWeight: 'bold',
                                            color: '#d32f2f', // Màu đỏ nổi bật
                                            WebkitTextFillColor: '#d32f2f',
                                        }
                                    }} 
                                />
                            </Box>
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        {/* 1. Danh sách khoản phải trả NCC */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Danh sách các khoản phải trả NCC
                        </Typography>
                        {(formData.payables || []).map((p, i) => {
                            if (p.payable_type === 'REFUND') return null; // Ẩn REFUND khỏi list này để hiện ở dưới
                            return (
                                <LiabilityItem 
                                    key={i} 
                                    item={p} 
                                    index={i} 
                                    isLocked={isFinanceLocked || p.status === 'PAID'} 
                                    supplierOptions={transportSupplierOptions} 
                                    onChange={handleListChange} 
                                    onMoneyChange={handleMoneyChange} 
                                    onRemove={removeRow} 
                                    onBillAction={() => handleBillAction(p, 'PAYABLE')} 
                                />
                            )
                        })}
                        <Button 
                            startIcon={<AddIcon />} 
                            onClick={() => addRow('payables')} 
                            disabled={isFinanceLocked} 
                            variant="outlined" size="small" color="warning" sx={{ mb: 3 }}
                        >
                            Thêm Công nợ NCC
                        </Button>

                        <Divider sx={{ my: 2 }} />
                        
                        {/* 2. Block riêng cho Hoàn tiền dịch vụ (Refunds) */}
                        <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: 2, border: '1px dashed #ed6c02' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Hoàn tiền dịch vụ (Service Refunds)
                            </Typography>
                            {(formData.payables || []).filter(p => p.payable_type === 'REFUND').map((refund, idx) => {
                                // Tìm đúng index gốc trong mảng formData.payables
                                const originalIndex = formData.payables.findIndex(p => p === refund);
                                return (
                                    <Box key={idx} sx={{ ...rowStyle, alignItems: 'flex-end', mb: 2 }}>
                                        <Box flex={1.5}>
                                            <CustomLabel title="Loại hoàn tiền" />
                                            <TextField size="small" fullWidth disabled value="REFUND" />
                                        </Box>
                                        <Box flex={2}>
                                            <CustomLabel title="TK Nhận (Khách/Đại lý)" />
                                            <TextField 
                                                size="small" fullWidth 
                                                value={refund.receiver_info || ''} 
                                                disabled={isFinanceLocked || refund.status === 'PAID'} 
                                                onChange={e => handleListChange('payables', originalIndex, 'receiver_info', e.target.value)} 
                                            />
                                        </Box>
                                        <Box flex={2}>
                                            <CustomLabel title="Lý do / Ghi chú" />
                                            <TextField 
                                                size="small" fullWidth 
                                                value={refund.note || ''} 
                                                disabled={isFinanceLocked || refund.status === 'PAID'} 
                                                onChange={e => handleListChange('payables', originalIndex, 'note', e.target.value)} 
                                            />
                                        </Box>
                                        <Box flex={1.5}>
                                            <CustomLabel title="Số tiền hoàn" />
                                            <CurrencyInput 
                                                value={{ 
                                                    original: refund.amount_original || 0, 
                                                    currency: refund.currency || 'VND', 
                                                    rate: refund.exchange_rate || 1, 
                                                    vnd: refund.amount || 0 
                                                }} 
                                                disabled={isFinanceLocked || refund.status === 'PAID'} 
                                                onChange={(obj) => handleMoneyChange('payables', originalIndex, 'amount', obj)} 
                                            />
                                        </Box>
                                        <Box flex={1}>
                                            <CustomLabel title="Trạng thái" />
                                            <TextField 
                                                select fullWidth size="small" 
                                                value={refund.status || 'UNPAID'} 
                                                disabled={isFinanceLocked} 
                                                onChange={e => handleListChange('payables', originalIndex, 'status', e.target.value)}
                                            >
                                                <MenuItem value="UNPAID">Chưa hoàn</MenuItem>
                                                <MenuItem value="PAID">Đã hoàn</MenuItem>
                                            </TextField>
                                        </Box>
                                        <Box flex={0.8}>
                                            <CustomLabel title="Bill" />
                                            <Button
                                                variant="outlined" fullWidth size="small"
                                                onClick={() => handleBillAction(refund, 'PAYABLE')}
                                                sx={{ height: '40px', textTransform: 'none', borderColor: refund.bill_image ? '#2e7d32' : 'rgba(0, 0, 0, 0.23)', color: refund.bill_image ? '#2e7d32' : 'rgba(0, 0, 0, 0.6)' }}
                                            >
                                                {refund.bill_image ? 'Xem Bill' : 'Upload'}
                                            </Button>
                                        </Box>
                                        <Box sx={{ pb: 0.5 }}>
                                            <IconButton onClick={() => removeRow('payables', originalIndex)} disabled={isFinanceLocked} color="error" size="small">
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                )
                            })}
                            <Button 
                                startIcon={<AddIcon />} 
                                // Gọi thẳng hành động addRow với type 'REFUND'
                                onClick={() => {
                                    setFormData(prev => ({
                                        ...prev,
                                        payables: [...(prev.payables || []), { payable_type: 'REFUND', amount: 0, status: 'UNPAID' }]
                                    }));
                                    setIsDirty(true);
                                }} 
                                disabled={isFinanceLocked} 
                                variant="outlined" size="small" color="warning"
                            >
                                Thêm yêu cầu hoàn tiền
                            </Button>
                        </Box>
                    </FormSection>

                    <Box sx={{ ...rowStyle, mt: 3 }}>
                        <Box flex={1}>
                            <Button 
                                variant="outlined" color="error" size="large" fullWidth 
                                disabled={isLockedByDone || isFinanceLocked || formData.booking_status === 'CANCELLED'} 
                                onClick={() => setCancelDialogOpen(true)}
                            >
                                {formData.booking_status === 'CANCELLED' ? 'ĐÃ HỦY' : 'HỦY BOOKING'}
                            </Button>
                        </Box>
                        <Box flex={1}><Button type="submit" variant="contained" size="large" fullWidth disabled={shouldDisableSave} sx={{ backgroundColor: '#007373', '&:hover': { backgroundColor: '#005c5c' } }}>LƯU DỮ LIỆU</Button></Box>
                    </Box>
                </form>

                <ConfirmDialog open={confirmModal.open} title={confirmModal.title} content={confirmModal.content} onClose={() => setConfirmModal({...confirmModal, open: false})} onConfirm={handleConfirmAction} confirmColor={confirmModal.confirmColor} />
                <BulkPaymentDialog 
                    open={bulkPayModal.open}
                    onClose={() => setBulkPayModal({...bulkPayModal, open: false})}
                    items={bulkPayModal.items}
                    type={bulkPayModal.type}
                    showToast={showToast}
                    onSuccess={() => {
                        if (isEditMode) loadBooking(formData.booking_code); 
                    }}
                />
                <ConfirmDialog 
                    open={custConflictDialog.open}
                    title="Cập nhật tên Khách hàng?"
                    content={
                        <Box>
                            <Typography>Số điện thoại <b>{custConflictDialog.phone}</b> đã tồn tại trong hệ thống.</Typography>
                            <Typography sx={{ mt: 1 }}>Tên hiện tại: <b>{custConflictDialog.existingName}</b></Typography>
                            <Typography sx={{ color: '#d32f2f', fontWeight: 'bold' }}>Tên mới nhập: {custConflictDialog.newName}</Typography>
                            <Typography sx={{ mt: 2, fontStyle: 'italic' }}>Bạn có muốn cập nhật Khách hàng này thành tên mới không?</Typography>
                        </Box>
                    }
                    confirmText="Cập nhật & Lưu"
                    cancelText="Xem lại"
                    confirmColor="warning"
                    onClose={() => setCustConflictDialog({ ...custConflictDialog, open: false })}
                    onConfirm={() => {
                        setCustConflictDialog({ ...custConflictDialog, open: false });
                        executeSubmit(); // Gọi lưu luôn, Backend sẽ tự update tên (nhờ bước sửa serializers.py)
                    }}
                />
                <Dialog 
                    open={passportPreview.open} 
                    onClose={() => setPassportPreview({ open: false, url: '' })} 
                    maxWidth="md"
                    PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none' } }} // Nền trong suốt để ảnh nổi bật
                >
                    <Box sx={{ position: 'relative', bgcolor: 'white', p: 1, borderRadius: 2 }}>
                        <IconButton 
                            onClick={() => setPassportPreview({ open: false, url: '' })} 
                            sx={{ position: 'absolute', top: 12, right: 12, bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: '#f5f5f5' } }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <img 
                            src={passportPreview.url} 
                            alt="Passport Preview" 
                            style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block', borderRadius: '4px' }} 
                        />
                    </Box>
                </Dialog>
                <BillDetailDialog open={billModal.open} onClose={() => setBillModal({ ...billModal, open: false })} billData={billModal.data} bookingData={billModal.bookingContext} />
                <TicketExportDialog open={ticketDialogOpen} onClose={() => setTicketDialogOpen(false)} bookingData={formData} />
                <InvoiceExportDialog open={invoiceDialogOpen} onClose={() => setInvoiceDialogOpen(false)} bookingData={formData} />
                <CancellationDialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} booking={formData} onSubmit={handleConfirmCancel} />
                <Snackbar open={toast.open} autoHideDuration={1000} onClose={handleCloseToast} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} sx={{ mt: 8 }}><Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', boxShadow: 3 }} variant="filled">{toast.message}</Alert></Snackbar>
            </Container>
        </ThemeProvider>
    );
}

export default BookingForm;